import { createContext, useContext, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Undo2 } from "lucide-react";
import { fmtTL } from "../../lib/format";
import { safePath } from "../../lib/nav";

export const COMPANIES = ["WILCO AŞ.", "WILCO BORDROLAMA", "WILCO ENDÜSTRİYEL"];
export const CompanyContext = createContext(COMPANIES[0]);
export const useCompany = () => useContext(CompanyContext);
export const coQs = (company) => `&company=${encodeURIComponent(company)}`;
export const fmtD = (d) => (d ? d.split("-").reverse().join(".") : "");

// Sekmeler arası geçiş (muavin ↔ yevmiye): mevcut adres `don` ile taşınır; Esc / İptal / Kaydet ile aynı filtrelere dönülür
export function useLedgerNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const here = `${location.pathname}${location.search}`;
  const returnTo = safePath(params.get("don"));
  const goBack = () => { if (returnTo) navigate(returnTo); };
  const goTo = (extra) => navigate(`/muhasebe?${new URLSearchParams({ firma: params.get("firma") || COMPANIES[0], ...extra, don: here }).toString()}`);
  const setFilters = (patch) => setParams((p) => { const n = new URLSearchParams(p); Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); return n; }, { replace: true });
  return { params, returnTo, goBack, goTo, setFilters };
}

// Dönüş şeridi: `don` varsa görünür; Esc ile geri döner
export function ReturnBar({ returnTo, onBack, label, testid = "ledger-return" }) {
  useEffect(() => {
    if (!returnTo) return undefined;
    const onKey = (e) => { if (e.key !== "Escape" || e.target.closest("[data-searchable]")) return; e.preventDefault(); e.stopImmediatePropagation(); onBack(); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [returnTo, onBack]);
  if (!returnTo) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-orange-100 border border-orange-200 text-orange-900 text-[11px] font-semibold" data-testid={testid} data-return-bar="1">
      {label} — ESC ile geri dön
      <button type="button" onClick={onBack} className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded border border-orange-300 bg-white hover:bg-orange-50" data-testid={`${testid}-back`}><Undo2 className="w-3 h-3" /> Geri Dön</button>
    </div>
  );
}

const pad = (n) => String(n).padStart(2, "0");
export const today = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
export const yearStart = () => `${new Date().getFullYear()}-01-01`;
export const yearEnd = () => `${new Date().getFullYear()}-12-31`;
export const KINDS = { grup: "Grup", ana: "Ana", detay: "Detay" };
export const inp = "px-2 py-1.5 text-xs border border-slate-300 rounded-md bg-white";
export const lbl = "text-[10px] uppercase tracking-widest text-slate-500 font-semibold";
export const btn = "inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-colors disabled:opacity-50";
export const btnDark = `${btn} bg-slate-900 text-white hover:bg-slate-800`;
export const btnLight = `${btn} border border-slate-300 bg-white text-slate-800 hover:bg-slate-100`;

export function Money({ v, bold, dim }) {
  if (!v) return <span className="text-slate-300">—</span>;
  return <span className={`${bold ? "font-bold" : ""} ${dim ? "text-slate-500" : ""}`}>{fmtTL(v)}</span>;
}

export function Balance({ v, inverse }) {
  if (!v) return <span className={inverse ? "text-slate-300" : "text-slate-300"}>0,00</span>;
  const pos = inverse ? "text-white" : "text-slate-900", neg = inverse ? "text-red-300" : "text-red-700";
  return <span className={v > 0 ? pos : neg}>{fmtTL(Math.abs(v))} <span className={`text-[9px] uppercase ${inverse ? "text-slate-300" : "text-slate-400"}`}>{v > 0 ? "B" : "A"}</span></span>;
}

export function KindBadge({ kind }) {
  const cls = kind === "detay" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : kind === "ana" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200";
  return <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase ${cls}`}>{KINDS[kind] || kind}</span>;
}

// Tarih aralığı filtresi (defterler ve mizan için ortak)
export function RangeFilter({ from, to, onFrom, onTo, testid = "range" }) {
  return (
    <>
      <label className={lbl}>Başlangıç<input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className={`${inp} block`} data-testid={`${testid}-from`} /></label>
      <label className={lbl}>Bitiş<input type="date" value={to} onChange={(e) => onTo(e.target.value)} className={`${inp} block`} data-testid={`${testid}-to`} /></label>
    </>
  );
}
